import { FEATURE_FLAGS_SCHEMA, FeatureFlags } from '@/@core/models/FeatureFlags';
import {
  CONFIG_RESPONSE_SCHEMA,
  ConfigResponse,
  DEFAULT_CONFIG,
  QA_RESOURCES_RESPONSE_SCHEMA,
  QA_SOURCES_RESPONSE_SCHEMA,
  QaChatRequest,
  QaResourcesResponse,
  QaSourcesRequest,
  QaSourcesResponse,
  TranslateRequest,
  TRANSLATION_RESOURCES_RESPONSE_SCHEMA,
  TRANSLATION_RESPONSE_SCHEMA,
  TranslationResourcesResponse,
  TranslationResponse,
  AgentWorkflowDetectionRequest,
  AgentWorkflowDetectionResponse,
  IN_CONTEXT_SOURCES_RESPONSE_SCHEMA,
  InContextSourcesResponseSchema,
} from '@/@core/models/api';
import { Collection } from '@/@core/models/api';
import {
  BACKEND_VERSION_SCHEMA,
  BackendVersionResponse,
} from '@/@core/models/api/BackendVersionResponse';
import { AccessibilityApiForm } from '@/@core/models/api/accessibilityFeedback';
import { FEATURE_SET_SCHEMA, FeatureSetResponse } from '@/@core/models/api/featureSetResponse';
import { FeedbackEndpoint, FeedbackRequest } from '@/@core/models/api/feedback';
import { UPLOAD_CONFIG_SCHEMA, UploadConfigResponse } from '@/@core/models/api/uploadConfig';
import { HTTP_CLIENT, HTTP_CLIENT_NLS } from '@/@core/services/api/httpClient';
import { handleRequestError } from '@/@core/services/api/utils/errorHandling';
import { validateResponseData } from '@/@core/services/api/utils/validateResponseData';
import {
  ConversationType,
  ConversationMessagesResponse,
} from '@/modules/AgenticChat/models/QaChatEntry';
import { Http } from '@aleph-alpha/lib-http';

const UI_VERSION: string = import.meta.env.VITE_APP_VERSION || 'unknown';
interface AgenticMessageRequest {
  content: string;
  conversationId: string;
  parentMessageId: string;
  attachments?: any[];
  collections?: Collection[];
  reasoningModelOverride?: string;
  deepResearchIsEnabled?: boolean;
  deepResearchId?: string;
  personality?: string;
  serverIds?: string[];
  think?: boolean;
  questionId?: string;
}

export class AssistantService {
  constructor(readonly httpClient: Http) {}
  async canvasEditAgent(body: any): Promise<any> {
    const response = await handleRequestError(
      async () => await this.httpClient.post<any>('skills/canvas-edit-agent', { body })
    );
    return response.data;
  }
  async featureFlags(): Promise<FeatureFlags> {
    const response = await handleRequestError(
      async () => await this.httpClient.get<FeatureFlags>('feature-flags')
    );

    return await validateResponseData(response, FEATURE_FLAGS_SCHEMA);
  }

  async featureSet(): Promise<FeatureSetResponse> {
    const response = await handleRequestError(
      async () => await this.httpClient.get<FeatureSetResponse>('feature-set')
    );

    return await validateResponseData(response, FEATURE_SET_SCHEMA);
  }

  async getBackendVersion(): Promise<BackendVersionResponse> {
    const response = await handleRequestError(
      async () => await this.httpClient.get<BackendVersionResponse>('version')
    );

    return await validateResponseData(response, BACKEND_VERSION_SCHEMA);
  }

  async config(): Promise<ConfigResponse> {
    const response = await handleRequestError(
      async () => await this.httpClient.get<ConfigResponse>('config')
    );

    return await validateResponseData(response, CONFIG_RESPONSE_SCHEMA);
  }

  async configWithDefaults(): Promise<ConfigResponse> {
    try {
      return await this.config();
    } catch (error: unknown) {
      // TODO: Add analytics/error tracking here if needed
      console.log('Error when getting config from API, using default values', error);
      return {
        ...DEFAULT_CONFIG,
        hasError: true,
      };
    }
  }

  async translate(body: TranslateRequest): Promise<TranslationResponse> {
    const response = await handleRequestError(
      async () =>
        await this.httpClient.post<TranslationResponse>('translate', {
          body,
        })
    );

    return await validateResponseData(response, TRANSLATION_RESPONSE_SCHEMA);
  }

  async translationResources(): Promise<TranslationResourcesResponse> {
    const response = await handleRequestError(
      async () => await this.httpClient.get<TranslationResourcesResponse>('translation/resources')
    );

    return await validateResponseData(response, TRANSLATION_RESOURCES_RESPONSE_SCHEMA);
  }

  async qaChat(body: QaChatRequest, signal: AbortSignal) {
    const response = await handleRequestError(
      async () =>
        await this.httpClient.post<ReadableStream>('qa/chat', {
          body: body,
          signal: signal,
          stream: true,
        })
    );

    return response.data;
  }

  async getQaSources(request: QaSourcesRequest): Promise<QaSourcesResponse> {
    const response = await handleRequestError(
      async () => await this.httpClient.get<QaSourcesResponse>(`qa/sources/${request.trace_id}`)
    );
    return await validateResponseData(response, QA_SOURCES_RESPONSE_SCHEMA);
  }

  async postQaSources(request: QaSourcesRequest): Promise<void> {
    await handleRequestError(
      async () => await this.httpClient.post<void>(`qa/sources/${request.trace_id}`)
    );
  }

  async qaResources(): Promise<QaResourcesResponse> {
    const response = await handleRequestError(
      async () => await this.httpClient.get<QaResourcesResponse>('qa/resources')
    );
    return await validateResponseData(response, QA_RESOURCES_RESPONSE_SCHEMA);
  }

  async qaUploadConfig(userId: string): Promise<UploadConfigResponse> {
    const response = await handleRequestError(
      async () => await this.httpClient.get<UploadConfigResponse>(`qa/upload-config/${userId}`)
    );

    return await validateResponseData(response, UPLOAD_CONFIG_SCHEMA);
  }

  async postFeedback(
    body: FeedbackRequest | AccessibilityApiForm,
    requestUrl: FeedbackEndpoint
  ): Promise<void> {
    const request = {
      ...body,
      ui_version: UI_VERSION,
    };

    await handleRequestError(
      async () =>
        await this.httpClient.post(requestUrl, {
          body: request,
        })
    );
  }

  async agentWorkflowDetection(
    body: AgentWorkflowDetectionRequest
  ): Promise<AgentWorkflowDetectionResponse> {
    const response = await handleRequestError(
      async () =>
        await this.httpClient.post<AgentWorkflowDetectionResponse>('skills/generate-agent', {
          body: body,
        })
    );
    return response.data;
  }
}
class AgenticAssistantService {
  constructor(readonly httpClient: Http) {}

  async createAgenticConversation(name?: string, signal?: AbortSignal) {
    const response = await handleRequestError(
      async () => await this.httpClient.post(`conversations?name=${name}`, { signal })
    );
    return response.data;
  }

  async getAllConversation(): Promise<ConversationType[]> {
    const response = await handleRequestError(
      async () => await this.httpClient.get('conversations')
    );
    return response.data as ConversationType[];
  }

  async getConversationMessages(conversationId: string): Promise<ConversationMessagesResponse> {
    const response = await handleRequestError(
      async () => await this.httpClient.get(`conversations/${conversationId}/messages`)
    );
    return response.data as ConversationMessagesResponse;
  }

  async deleteConversation(conversationId: string) {
    const response = await handleRequestError(
      async () => await this.httpClient.delete(`conversations/${conversationId}`)
    );
    return response.data;
  }

  async renameConversation(conversationId: string, name: string) {
    const response = await handleRequestError(
      async () => await this.httpClient.patch(`conversations/${conversationId}?name=${name}`)
    );
    return response.data;
  }

  async createAgenticMessage(body: AgenticMessageRequest, signal: AbortSignal) {
    const response = await handleRequestError(
      async () =>
        await this.httpClient.post<ReadableStream>(
          `conversations/${body.conversationId}/messages/v2`,
          {
            body: {
              content: body.content,
              parent_message_id: body.parentMessageId || undefined,
              attachments: body.attachments || undefined,
              collections: body.collections || undefined,
              reasoning_model_override: body.reasoningModelOverride || undefined,
              deep_research: body.deepResearchIsEnabled || undefined,
              deep_research_id: body.deepResearchId || undefined,
              personality: body.personality || undefined,
              server_ids: body.serverIds || undefined,
              think: body.think,
              message_id: body.questionId,
            },
            signal,
            stream: true,
          }
        )
    );

    return response.data;
  }

  async getInContextSources(
    conversationId: string,
    messageId: string
  ): Promise<InContextSourcesResponseSchema> {
    await handleRequestError(
      async () =>
        await this.httpClient.post<void>(
          `conversations/${conversationId}/messages/${messageId}/sources`
        )
    );

    const getSources = async (): Promise<InContextSourcesResponseSchema> => {
      try {
        const response = await handleRequestError(
          async () =>
            await this.httpClient.get(
              `conversations/${conversationId}/messages/${messageId}/sources`,
              { retry: undefined }
            )
        );
        return await validateResponseData(response, IN_CONTEXT_SOURCES_RESPONSE_SCHEMA);
      } catch {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        return getSources();
      }
    };
    return getSources();
  }
}

export const ASSISTANT_SERVICE = new AssistantService(HTTP_CLIENT);
export const AGENTIC_ASSISTANT_SERVICE = new AgenticAssistantService(HTTP_CLIENT_NLS);
